/**
 * User-facing messages for a failed password reset or change. Supabase
 * AuthErrors carry a stable `code` (and, for a weak password, `reasons`);
 * anything else falls back to its message.
 */

const CURRENT_PASSWORD_WRONG = 'Your current password is incorrect.';
const RATE_LIMITED = 'Too many attempts. Wait a minute, then try again.';

const MESSAGES_BY_CODE: Record<string, string> = {
  same_password: 'Choose a password different from your current one.',
  current_password_mismatch: CURRENT_PASSWORD_WRONG,
  current_password_required: CURRENT_PASSWORD_WRONG,
  reauthentication_needed: 'For your security, sign out, sign in again, then change your password.',
  insufficient_aal: 'Enter the code from your authenticator app first.',
  over_email_send_rate_limit: 'Too many emails were sent. Wait a few minutes, then try again.',
  over_request_rate_limit: RATE_LIMITED,
  too_many_requests: RATE_LIMITED,
};

const WEAK_PASSWORD_MESSAGES: Record<string, string> = {
  pwned: 'This password has appeared in a data breach. Choose a different one.',
  length: 'This password is too short.',
  characters: 'Use a mix of character types: upper- and lowercase letters, digits and symbols.',
};

const SESSION_MISSING_MESSAGE =
  'Your reset link has expired. Request a new reset link from the sign-in page.';
const FALLBACK_MESSAGE = 'Something went wrong. Please try again.';

function field(error: unknown, name: string): unknown {
  return typeof error === 'object' && error !== null && name in error
    ? (error as Record<string, unknown>)[name]
    : undefined;
}

function weakPasswordMessage(error: unknown): string {
  const reasons = field(error, 'reasons');
  const known = Array.isArray(reasons)
    ? reasons.find(
        (reason): reason is string => typeof reason === 'string' && reason in WEAK_PASSWORD_MESSAGES
      )
    : undefined;
  return (known && WEAK_PASSWORD_MESSAGES[known]) ?? 'This password is too weak.';
}

export function passwordErrorMessage(error: unknown): string {
  const code = field(error, 'code');
  if (code === 'weak_password') return weakPasswordMessage(error);
  if (typeof code === 'string' && MESSAGES_BY_CODE[code]) return MESSAGES_BY_CODE[code];
  if (field(error, 'name') === 'AuthSessionMissingError') return SESSION_MISSING_MESSAGE;
  return error instanceof Error && error.message ? error.message : FALLBACK_MESSAGE;
}
